import { useForm } from "@inertiajs/react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { bizminer_admin_organization_sso_config_path } from "@/routes";
import type { BizminerAdminSsoConfig } from "@/types/serializers";

const PROVIDERS = [
  { value: "google", label: "Google" },
  { value: "entra_id", label: "Microsoft Entra ID" },
] as const;

interface Props {
  onClose: () => void;
  organizationId: number;
  ssoConfig: BizminerAdminSsoConfig;
}

export default function SsoEditDialog({
  onClose,
  organizationId,
  ssoConfig,
}: Props) {
  const { data, setData, patch, processing, errors, transform } = useForm({
    sso_enabled: ssoConfig.sso_enabled ?? false,
    sso_provider: ssoConfig.sso_provider ?? "",
    sso_client_id: ssoConfig.sso_client_id ?? "",
    sso_client_secret: "",
    sso_tenant_id: ssoConfig.sso_tenant_id ?? "",
    sso_allowed_domains: ssoConfig.sso_allowed_domains?.join(", ") ?? "",
  });

  transform((formData) => {
    const transformed: Record<string, unknown> = {
      ...formData,
      sso_allowed_domains: formData.sso_allowed_domains
        .split(",")
        .map((d: string) => d.trim())
        .filter(Boolean),
    };

    if (!formData.sso_client_secret) {
      delete transformed.sso_client_secret;
    }

    return { sso_config: transformed };
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    patch(bizminer_admin_organization_sso_config_path(organizationId), {
      preserveScroll: true,
      onSuccess: onClose,
    });
  };

  const showMicrosoftFields = data.sso_provider === "entra_id";

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[500px] text-foreground">
        <DialogHeader>
          <DialogTitle>Edit SSO Configuration</DialogTitle>
          <DialogDescription>
            Configure single sign-on for this organization
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="sso_enabled">Enable SSO</Label>
            <Switch
              id="sso_enabled"
              checked={data.sso_enabled}
              onCheckedChange={(checked) => setData("sso_enabled", checked)}
            />
          </div>

          {data.sso_enabled && (
            <>
              <div>
                <Label htmlFor="sso_provider">Provider</Label>
                <Select
                  value={data.sso_provider}
                  onValueChange={(value) => setData("sso_provider", value)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select provider" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROVIDERS.map((provider) => (
                      <SelectItem key={provider.value} value={provider.value}>
                        {provider.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.sso_provider && (
                  <p className="text-sm text-destructive mt-1">
                    {errors.sso_provider}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="sso_client_id">Client ID</Label>
                <Input
                  id="sso_client_id"
                  value={data.sso_client_id}
                  onChange={(e) => setData("sso_client_id", e.target.value)}
                  className="mt-1 font-mono"
                  placeholder="Enter client ID"
                />
                {errors.sso_client_id && (
                  <p className="text-sm text-destructive mt-1">
                    {errors.sso_client_id}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="sso_client_secret">
                  Client Secret
                  {ssoConfig.sso_client_secret_masked && (
                    <span className="text-muted-foreground font-normal ml-2">
                      (leave blank to keep current:{" "}
                      {ssoConfig.sso_client_secret_masked})
                    </span>
                  )}
                </Label>
                <Input
                  id="sso_client_secret"
                  type="password"
                  value={data.sso_client_secret}
                  onChange={(e) => setData("sso_client_secret", e.target.value)}
                  className="mt-1 font-mono"
                  placeholder={
                    ssoConfig.sso_client_secret_masked
                      ? "••••••••"
                      : "Enter client secret"
                  }
                />
                {errors.sso_client_secret && (
                  <p className="text-sm text-destructive mt-1">
                    {errors.sso_client_secret}
                  </p>
                )}
              </div>

              {showMicrosoftFields && (
                <div>
                  <Label htmlFor="sso_tenant_id">Tenant ID</Label>
                  <Input
                    id="sso_tenant_id"
                    value={data.sso_tenant_id}
                    onChange={(e) => setData("sso_tenant_id", e.target.value)}
                    className="mt-1 font-mono"
                    placeholder="Enter Microsoft tenant ID"
                  />
                  {errors.sso_tenant_id && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.sso_tenant_id}
                    </p>
                  )}
                </div>
              )}

              <div>
                <Label htmlFor="sso_allowed_domains">
                  Allowed Email Domains
                  <span className="text-muted-foreground font-normal ml-2">
                    (comma-separated, leave empty for all)
                  </span>
                </Label>
                <Input
                  id="sso_allowed_domains"
                  value={data.sso_allowed_domains}
                  onChange={(e) =>
                    setData("sso_allowed_domains", e.target.value)
                  }
                  className="mt-1"
                  placeholder="example.com, company.org"
                />
              </div>
            </>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={processing}>
              {processing ? "Saving..." : "Save Configuration"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
